<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ShieldCheck } from '@lucide/vue'
import AppDialog from '@/components/app/AppDialog.vue'
import ErrorAlert from '@/components/app/ErrorAlert.vue'
import MfaVerifyPanel from '@/components/app/MfaVerifyPanel.vue'
import { mfaStatus, type MfaStatus } from '@/modules/auth/authApi'
import { settleStepUp, useStepUp } from '@/modules/auth/stepUp'
import { useUser } from '@/composables/useUser'
import { apiErrorMessage } from '@/lib/errors'

/**
 * Modale de **step-up** : ouverte par le client API sur `STEP_UP_REQUIRED`, elle vérifie un
 * facteur sans quitter l'écran ; la requête refusée est rejouée ensuite. Montée une fois
 * dans `App.vue`.
 */
const { pending } = useStepUp()
const { refreshAssurance } = useUser()
const router = useRouter()

const loading = ref(false)
const loadError = ref('')
const status = ref<MfaStatus | null>(null)

watch(pending, async (request) => {
  if (!request) return
  loading.value = true
  loadError.value = ''
  status.value = null
  try {
    status.value = await mfaStatus()
  } catch (err) {
    loadError.value = apiErrorMessage(err, 'État de la double authentification inaccessible.')
  } finally {
    loading.value = false
  }
})

async function onVerified(): Promise<void> {
  await refreshAssurance()
  settleStepUp(true)
}

function onOpenChange(open: boolean): void {
  if (!open) settleStepUp(false)
}

/** L'enrôlement TOTP a sa propre page (QR, codes de secours) : on y va, cible en poche. */
function goEnroll(): void {
  const target = window.location.pathname + window.location.search
  settleStepUp(false)
  void router.push({ name: 'mfa-enroll', query: { redirect: target } })
}
</script>

<template>
  <AppDialog
    :open="!!pending"
    title="Renforcer la connexion"
    tint="sage"
    :icon="ShieldCheck"
    size="sm"
    @update:open="onOpenChange"
  >
    <template v-if="pending">
      <p v-if="loading" class="text-sm text-muted-foreground">Chargement…</p>
      <ErrorAlert v-else-if="loadError" :message="loadError" />
      <MfaVerifyPanel v-else-if="status" :status="status" @verified="onVerified" @enroll="goEnroll">
        <template #proposal>Configurez une application d’authentification pour continuer.</template>
      </MfaVerifyPanel>
    </template>
  </AppDialog>
</template>
